import { useState } from 'react';
import type { CounselorMemo, Session } from '@/domain/schema/v1';
import { detectTranscript, splitCombined } from '@/domain/transcript/detect';
import { useAutosave } from '../../hooks/useAutosave';
import { useDebouncedSave } from '../../hooks/useDebouncedSave';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { SaveIndicator, TextInput } from '../../components/fields';

/** 새 회기 자료 입력 (UX_FLOW §8). 모든 입력은 자동 저장되고, 화면을 닫아도 그대로 남는다. */
export function InputSection({ session, onChanged }: { session: Session; onChanged: () => void }) {
  const svc = useService();
  const { data } = useLoad(
    async () => ({
      galaxy: await svc.getRawTranscriptText(session.samsungTranscriptId),
      iphone: await svc.getRawTranscriptText(session.appleTranscriptId),
    }),
    [svc, session.id],
  );

  return (
    <>
      <div className="card">
        <SessionInfoFields session={session} />
      </div>
      {data && <TranscriptInputs sessionId={session.id} initial={data} onChanged={onChanged} />}
      <MemoBox sessionId={session.id} />
    </>
  );
}

/**
 * 회기 정보 (상담 날짜·시간·장소): 처리 전후·승인 후에도 고칠 수 있다 (상담 날짜는 기록 내용이 아니라 회기 정보).
 * 바꾼 이력은 저장소 이력에 남는다. 앞뒤 회기와 날짜 순서가 맞지 않으면 알려준다.
 */
export function SessionInfoFields({ session, onSaved }: { session: Session; onSaved?: () => void }) {
  const svc = useService();
  const meta = useAutosave<Session>(session, async (patch, rev) => {
    const r = await svc.updateSession(session.id, patch, rev);
    onSaved?.();
    return r;
  });
  const v = meta.value!;
  const { data: siblings } = useLoad(() => svc.listSessions(session.clientId), [svc, session.clientId]);
  const prev = siblings?.filter((s) => s.sessionNumber < session.sessionNumber).at(-1);
  const next = siblings?.find((s) => s.sessionNumber > session.sessionNumber);
  const orderWarn =
    prev && v.sessionDate < prev.sessionDate
      ? `${prev.sessionNumber}회기(${prev.sessionDate})보다 앞 날짜입니다`
      : next && v.sessionDate > next.sessionDate
        ? `${next.sessionNumber}회기(${next.sessionDate})보다 뒤 날짜입니다`
        : null;
  return (
    <>
      <div className="card-head">
        <h2>회기 정보</h2>
        <SaveIndicator status={meta.status} error={meta.error} />
      </div>
      <TextInput
        label="상담 날짜"
        type="date"
        value={v.sessionDate}
        onChange={(x) => x && meta.set('sessionDate', x)}
        hint="실제로 상담한 날짜 (기록을 쓴 날이 아님)"
      />
      {orderWarn && (
        <p role="alert" className="warn small">
          날짜 순서 확인: {orderWarn}. 다른 회기 날짜가 잘못됐다면 그 회기 화면의 '회기 정보'에서 고치세요.
        </p>
      )}
      <TextInput
        label="상담 시간(분)"
        type="number"
        value={String(v.durationMinutes ?? '')}
        onChange={(x) => meta.set('durationMinutes', Number(x) > 0 ? Math.round(Number(x)) : undefined)}
      />
      <TextInput
        label="장소"
        value={v.location}
        onChange={(x) => meta.set('location', x.slice(0, 100) || undefined)}
        hint="비우면 설정의 기본 상담 장소가 문서에 들어갑니다"
      />
    </>
  );
}

function TranscriptInputs({
  sessionId,
  initial,
  onChanged,
}: {
  sessionId: string;
  initial: { galaxy: string; iphone: string };
  onChanged: () => void;
}) {
  const svc = useService();
  const [galaxy, setGalaxy] = useState(initial.galaxy);
  const [iphone, setIphone] = useState(initial.iphone);
  const gSave = useDebouncedSave<string>(async (t) => void (await svc.saveRawTranscript(sessionId, 'samsung_raw', t)));
  const iSave = useDebouncedSave<string>(async (t) => void (await svc.saveRawTranscript(sessionId, 'apple_raw', t)));

  const combinedIn = [galaxy, iphone].find((t) => detectTranscript(t).kind === 'combined');
  const split = combinedIn ? splitCombined(combinedIn) : null;

  return (
    <div className="card">
      <h2>전사</h2>
      <p className="muted small">
        Galaxy와 iPhone 전사를 그대로 붙여넣거나 텍스트 파일을 고르세요. 원본은 수정 없이 보관됩니다. 화자 번호는
        상담자/내담자로 해석하지 않습니다.
      </p>
      {split && (
        <div className="banner">
          두 기기의 전사가 한 곳에 함께 들어 있습니다.
          <button
            type="button"
            onClick={() => {
              setGalaxy(split.galaxy);
              setIphone(split.iphone);
              gSave.schedule(split.galaxy);
              iSave.schedule(split.iphone);
              void Promise.all([gSave.flush(), iSave.flush()]).then(onChanged);
            }}
          >
            Galaxy / iPhone으로 나누기
          </button>
        </div>
      )}
      <TranscriptBox label="Galaxy 전사" value={galaxy} save={gSave} onChange={setGalaxy} />
      <TranscriptBox label="iPhone 전사" value={iphone} save={iSave} onChange={setIphone} />
    </div>
  );
}

function TranscriptBox(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  save: ReturnType<typeof useDebouncedSave<string>>;
}) {
  const d = detectTranscript(props.value);
  const info =
    d.kind === 'galaxy'
      ? `Galaxy 형식 · ${d.galaxyTurns}턴`
      : d.kind === 'iphone'
        ? `iPhone 형식 · ${d.iphoneBlocks}블록`
        : d.kind === 'combined'
          ? '두 기기 전사가 섞여 있음'
          : d.kind === 'unknown'
            ? '형식을 알 수 없음 (원문은 그대로 저장됩니다)'
            : '';
  const len = d.lastTimestampMs ? ` · 약 ${Math.round(d.lastTimestampMs / 60000)}분` : '';
  const set = (t: string) => {
    props.onChange(t);
    props.save.schedule(t);
  };
  return (
    <div className="field">
      <div className="field-label">
        <span>{props.label}</span>
        <SaveIndicator status={props.save.status} error={props.save.error} onRetry={() => void props.save.flush()} />
      </div>
      <textarea aria-label={props.label} rows={6} value={props.value} onChange={(e) => set(e.target.value)} />
      <div className="row small">
        <span className="muted">
          {info}
          {len}
        </span>
        <label className="file-pick">
          파일 선택
          <input
            type="file"
            accept=".txt,text/plain"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void f.text().then(set);
            }}
          />
        </label>
      </div>
    </div>
  );
}

/** 상담자 메모: 상담자 관찰(COUNSELOR_OBSERVATION)로만 저장된다 */
export function MemoBox({ sessionId }: { sessionId: string }) {
  const svc = useService();
  const { data } = useLoad(() => svc.ensureMemo(sessionId), [svc, sessionId]);
  if (!data) return null;
  return <MemoEditor memo={data} />;
}

function MemoEditor({ memo }: { memo: CounselorMemo }) {
  const svc = useService();
  const a = useAutosave<CounselorMemo>(memo, (patch, rev) => svc.saveMemo(memo.sessionId, patch.text ?? '', rev));
  return (
    <div className="card">
      <div className="card-head">
        <h2>상담자 메모</h2>
        <SaveIndicator status={a.status} error={a.error} />
      </div>
      <p className="muted small">
        표정 변화, 침묵의 특성, 현장에서 받은 인상, 다음에 확인할 점 등. 상담자 관찰로 기록됩니다.
      </p>
      <textarea
        aria-label="상담자 메모"
        rows={5}
        value={a.value?.text ?? ''}
        onChange={(e) => a.set('text', e.target.value)}
      />
    </div>
  );
}
